import { AiExtractionResponse, EstateFeaturesPrefill } from './types'

const ESTATE_FEATURE_KEYS: (keyof EstateFeaturesPrefill)[] = [
  'equippedKitchen',
  'balconyTerrace',
  'guestToilet',
  'basement',
  'elevator',
  'handicappedAccessible',
  'sharedGarden',
]

const NUMERIC_KEYS = [
  'livingSpace',
  'room',
  'noBathrooms',
  'noBedrooms',
  'usableArea',
  'plotArea',
  'constructionYear',
  'constructionLastModernization',
  'thermalCharacteristic',
  'noParkingSpaces',
] as const satisfies ReadonlyArray<keyof AiExtractionResponse>

const STRING_KEYS = [
  'buildingType',
  'constructionCondition',
  'constructionHeatingType',
  'street',
  'houseNumber',
  'city',
  'postcode',
  'efficiencyClass',
  'interiorQuality',
  'parkingSpaceType',
] as const satisfies ReadonlyArray<keyof AiExtractionResponse>

/** Accepts JSON numbers, numeric strings (e.g. BigDecimal as string), and German-style decimals. */
export const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null) {
    return undefined
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  if (trimmed === '') {
    return undefined
  }

  if (trimmed.includes(',')) {
    const normalized = trimmed.split('.').join('').replace(',', '.')
    const n = Number(normalized)
    return Number.isFinite(n) ? n : undefined
  }

  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

const isTrueEstateFeatureValue = (value: unknown): boolean => value === true

const collectEstateFeaturesFromRecord = (record: Record<string, unknown>): EstateFeaturesPrefill | undefined => {
  const next: Partial<EstateFeaturesPrefill> = {}
  for (const key of ESTATE_FEATURE_KEYS) {
    if (isTrueEstateFeatureValue(record[key])) {
      next[key] = true
    }
  }
  return Object.keys(next).length > 0 ? (next as EstateFeaturesPrefill) : undefined
}

const mergeEstateFeaturePrefills = (
  a: EstateFeaturesPrefill | undefined,
  b: EstateFeaturesPrefill | undefined
): EstateFeaturesPrefill | undefined => {
  if (!a && !b) {
    return undefined
  }
  const next: Partial<EstateFeaturesPrefill> = {}
  for (const key of ESTATE_FEATURE_KEYS) {
    if (a?.[key] === true || b?.[key] === true) {
      next[key] = true
    }
  }
  return Object.keys(next).length > 0 ? (next as EstateFeaturesPrefill) : undefined
}

const normalizeEstateFeaturesPayload = (raw: unknown): EstateFeaturesPrefill | undefined => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  return collectEstateFeaturesFromRecord(raw as Record<string, unknown>)
}

const parseOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const parseOptionalStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined
  }
  const strings = value.filter((item): item is string => typeof item === 'string')
  return strings.length > 0 ? strings : undefined
}

const assignParsed = <V>(
  out: AiExtractionResponse,
  source: Record<string, unknown>,
  key: keyof AiExtractionResponse,
  parse: (raw: unknown) => V | undefined
) => {
  const v = parse(source[String(key)])
  if (v !== undefined) {
    Object.assign(out, { [key]: v })
  }
}

export const normalizeAiExtractionResponse = (raw: unknown): AiExtractionResponse => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }

  const o = raw as Record<string, unknown>
  const out: AiExtractionResponse = {}

  for (const key of NUMERIC_KEYS) {
    assignParsed(out, o, key, parseOptionalNumber)
  }

  if (out.room === undefined) {
    const noRooms = parseOptionalNumber(o.noRooms)
    if (noRooms !== undefined) {
      out.room = noRooms
    }
  }

  for (const key of STRING_KEYS) {
    assignParsed(out, o, key, parseOptionalString)
  }

  assignParsed(out, o, 'constructionEnergySource', parseOptionalStringArray)

  const fromNested = normalizeEstateFeaturesPayload(o.estateFeatures)
  const fromTopLevel = collectEstateFeaturesFromRecord(o)
  const estateFeatures = mergeEstateFeaturePrefills(fromNested, fromTopLevel)
  if (estateFeatures !== undefined) {
    out.estateFeatures = estateFeatures
  }

  return out
}
